import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person, PersonId, PersonType } from '@core/data-access/models/person.model';
import {
  TicketAssignmentStoreState,
  TicketHolderForms,
  TicketHolderQuestionnaireForms,
  TicketHolders
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import { removeQuestionnaireFormFields } from '@core/utils/form-fields/form-field-helpers';
import { TariffUuid } from '@products/models/tariff.model';
import { find, groupBy, isEmpty, isNil, keyBy, mapValues, merge, omit, pickBy, reduce, some, values } from 'lodash';

export const addNewTicketHolder = (state: TicketAssignmentStoreState, person: Person) => {
  if (isNil(person)) {
    return state;
  }

  const { forms } = state;
  const selectedTariffUuid = state.selectedTariffUuid;

  return {
    ...state,
    ticketHolders: {
      ...state.ticketHolders,
      [person.uuid]: {
        person,
        formFields: {}
      }
    },
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms[FormName.TicketHolder],
        [selectedTariffUuid]: { person, formFields: {} }
      },
      [FormName.TicketHolderQuestionnaire]: omit(forms[FormName.TicketHolderQuestionnaire], selectedTariffUuid)
    }
  };
};

export const addTicketHolderAndSetFormFields = (
  state: TicketAssignmentStoreState,
  person: Person,
  formFields: FormFields
) => {
  if (isNil(person) || isEmpty(formFields)) {
    return state;
  }

  const { ticketHolders } = state;

  return {
    ...state,
    ticketHolders: {
      ...ticketHolders,
      [person.uuid]: {
        person,
        formFields
      }
    }
  };
};

export const assignTicketHolder = (state: TicketAssignmentStoreState, person: Person, formFields: FormFields) => {
  {
    if (isNil(person)) {
      return state;
    }

    const { forms } = state;
    const selectedTariffUuid = state.selectedTariffUuid;

    const assignedTicketHolderFormFields: FormFields = removeQuestionnaireFormFields(formFields);

    return {
      ...state,
      forms: {
        ...forms,
        [FormName.TicketHolder]: {
          ...forms[FormName.TicketHolder],
          [selectedTariffUuid]: { person, formFields: assignedTicketHolderFormFields }
        }
      }
    };
  }
};

export const unassignDeletedTicketHolder = (state: TicketAssignmentStoreState, deletedPerson: Person) => {
  if (isNil(deletedPerson)) {
    return state;
  }

  const { forms } = state;

  const unassignedTicketHolderForms = reduce(
    pickBy(forms[FormName.TicketHolder], ({ person: assignedPerson }) => assignedPerson?.uuid === deletedPerson.uuid),
    (deletedPersonForms, _, tariffUuid) => {
      deletedPersonForms[tariffUuid] = {
        person: null,
        formFields: {}
      };

      return deletedPersonForms;
    },
    {}
  );

  return {
    ...state,
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms[FormName.TicketHolder],
        ...unassignedTicketHolderForms
      }
    }
  };
};

export const updateTicketHoldersToSavedTicketHolders = (
  state: TicketAssignmentStoreState,
  createdPersons: Person[],
  removeTicketHolders: boolean
) => {
  const { forms } = state;

  if (isEmpty(createdPersons) || isEmpty(forms[FormName.TicketHolder])) {
    return state;
  }

  const updatedTicketHolders = removeTicketHolders
    ? pickBy(state.ticketHolders, ({ person }) => !some(createdPersons, ({ uuid }) => uuid === person.uuid))
    : mapValues(state.ticketHolders, (ticketHolder) => {
        const { person, formFields } = ticketHolder;
        const createdPerson = find(createdPersons, ({ uuid }) => uuid === person.uuid);

        if (isNil(createdPerson)) {
          return ticketHolder;
        }

        return {
          formFields,
          person: {
            ...person,
            id: createdPerson.id
          }
        };
      });

  const groupedPersonsByType = groupBy(createdPersons, 'type');
  const groupedByTypeAndMappedByUuid = mapValues(groupedPersonsByType, (type) =>
    mapValues(keyBy(type, 'uuid'), ({ id }): PersonId => id)
  ) as Record<PersonType, Record<TariffUuid, PersonId>>;

  const mappedPersonUuids = merge({}, ...values(groupedByTypeAndMappedByUuid));

  const updatedTicketHolderForms: TicketHolderForms = reduce(
    forms[FormName.TicketHolder],
    (updatedTicketHolderForms, { person, formFields }, tariffUuid) => {
      const personId = mappedPersonUuids[person?.uuid];

      if (isNil(personId)) {
        return updatedTicketHolderForms;
      }

      updatedTicketHolderForms[tariffUuid] = {
        formFields,
        person: {
          ...person,
          id: personId
        }
      };

      return updatedTicketHolderForms;
    },
    {}
  );

  if (
    !isNil(groupedByTypeAndMappedByUuid[PersonType.BuyerIsHolder]) ||
    isEmpty(forms[FormName.TicketHolderQuestionnaire])
  ) {
    return {
      ...state,
      ticketHolders: updatedTicketHolders,
      forms: {
        ...forms,
        [FormName.TicketHolder]: {
          ...forms[FormName.TicketHolder],
          ...updatedTicketHolderForms
        }
      }
    };
  }

  const updatedTicketHolderQuestionnaireForms: TicketHolderQuestionnaireForms = reduce(
    forms[FormName.TicketHolderQuestionnaire],
    (updatedTicketHolderQuestionnaireForms, { person, formFields }, tariffUuid) => {
      const personId = mappedPersonUuids[person.uuid];

      if (isNil(personId)) {
        return updatedTicketHolderQuestionnaireForms;
      }

      updatedTicketHolderQuestionnaireForms[tariffUuid] = {
        formFields,
        person: {
          ...person,
          id: personId
        }
      };

      return updatedTicketHolderQuestionnaireForms;
    },
    {}
  );

  return {
    ...state,
    ticketHolders: updatedTicketHolders,
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms[FormName.TicketHolder],
        ...updatedTicketHolderForms
      },
      [FormName.TicketHolderQuestionnaire]: {
        ...forms[FormName.TicketHolderQuestionnaire],
        ...updatedTicketHolderQuestionnaireForms
      }
    }
  };
};

export const setTicketHolderFields = (state: TicketAssignmentStoreState, person: Person, formFields: FormFields) => {
  if (isNil(person)) {
    return state;
  }

  const { id, uuid } = person;

  const isSavedTicketHolder = !isNil(id);

  if (isSavedTicketHolder) {
    return state;
  }

  if (isNil(state.ticketHolders[uuid])) {
    return {
      ...state,
      ticketHolders: {
        ...state.ticketHolders,
        [uuid]: {
          person,
          formFields
        }
      }
    };
  }

  const updatedTicketHolders: TicketHolders = reduce(
    formFields,
    (updatedTicketHolders, formField, formFieldKey) => {
      const clonedFormField = state.ticketHolders[uuid].formFields[formFieldKey];

      if (isNil(updatedTicketHolders[uuid])) {
        updatedTicketHolders[uuid] = state.ticketHolders[uuid];
      }

      updatedTicketHolders[uuid].formFields[formFieldKey] = isNil(clonedFormField)
        ? formField
        : {
            ...clonedFormField,
            ...formField
          };

      return updatedTicketHolders;
    },
    {}
  );

  return {
    ...state,
    ticketHolders: {
      ...state.ticketHolders,
      ...updatedTicketHolders
    }
  };
};
